/**
 * Client for the GTFS network store API (`/api/network/*`, PRD-01 + PRD-04).
 *
 * Backend-heavy: the browser holds only the viewport + selection. Mirrors the auth +
 * base-url pattern in `spatialAnalysisApi.ts`. All viewport queries accept an
 * `AbortSignal` so the display layers can cancel in-flight requests on rapid pan/zoom.
 */

import { atlasBackendApi } from '../api/atlasBackendClient';

import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';

const API_BASE = `${getAtlasApiBaseUrl()}/api/network`;

/** Read the atlas auth token from localStorage (matches spatialAnalysisApi). */
function getAuthToken(): string {
  const tokenString = localStorage.getItem('atlas_authToken');
  if (!tokenString) {
    throw new Error('No authentication token found. Please log in first.');
  }
  try {
    const parsed = JSON.parse(tokenString);
    if (parsed?.access_token) return parsed.access_token;
  } catch {
    // not JSON — use the raw string
  }
  return tokenString;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}` };
}

/**
 * Convert an uploaded UK BODS TransXChange file (XML, or a zip of TransXChange
 * XMLs) to a GTFS zip via the backend, returned as a File ready for the existing
 * GTFS import flow. Lets users drag-drop BODS timetables straight into Fixed Routes.
 */
export async function convertTransXChangeToGtfs(file: File): Promise<File> {
  const form = new FormData();
  form.append('file', file);
  try {
    const res = await atlasBackendApi.post(`${API_BASE}/transxchange/to-gtfs`, form, {
      headers: authHeaders(),
      responseType: 'blob',
      timeout: 180000,
    });
    const base = file.name.replace(/\.[^.]+$/, '') || 'transxchange';
    return new File([res.data as Blob], `${base}.gtfs.zip`, { type: 'application/zip' });
  } catch (err) {
    // With responseType 'blob', an error body comes back as a Blob — read its detail.
    const e = err as { response?: { data?: unknown; status?: number }; code?: string; message?: string };
    let detail = '';
    if (e.response?.data instanceof Blob) {
      const txt = await e.response.data.text();
      try { detail = JSON.parse(txt)?.detail || txt; } catch { detail = txt; }
    }
    if (e.code === 'ECONNABORTED') {
      detail = 'TransXChange conversion timed out — the file is likely too large for in-browser import.';
    }
    throw new Error(detail || e.message || 'TransXChange conversion failed');
  }
}

export interface FeedUploadResult {
  feedId: string;
  state: string;
  sourceFormat?: 'gtfs' | 'transxchange';
}

/**
 * Upload a GTFS zip or UK BODS TransXChange file (XML, or a zip of TransXChange XMLs)
 * into the persistent network store as a hosted custom feed (`POST /feeds/upload`).
 * Backend sniffs the format, converts TransXChange→GTFS, and ingests async — poll
 * `getFeedIngestStatus`. This is how a dropped/exported BODS feed becomes browsable.
 */
export async function uploadFeed(file: File, slug: string): Promise<FeedUploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('slug', slug);
  const res = await atlasBackendApi.post<FeedUploadResult>(`${API_BASE}/feeds/upload`, form, {
    headers: authHeaders(),
    timeout: 600000, // national feeds are large; the ingest itself runs async server-side
  });
  return res.data;
}

export interface FeedIngestStatus {
  state: 'ingesting' | 'ready' | 'failed' | string;
  step?: string;
  reason?: string;
  error?: string;
  version?: string;
  counts?: Record<string, number>;
}

export async function getFeedIngestStatus(feedId: string, signal?: AbortSignal): Promise<FeedIngestStatus> {
  const res = await atlasBackendApi.get<FeedIngestStatus>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/ingest-status`,
    { headers: authHeaders(), timeout: TIMEOUT, signal },
  );
  return res.data;
}

// --- editable network versions (scenarios / deltas) (PRD-05c) ---------------

export interface NetworkVersionSummary {
  scenarioId: string;
  name: string;
  baseVersion?: string | null;
  createdBy?: string;
  createdAt?: number;
  updatedAt?: number;
  summary?: { editedCount: number; addedCount: number; removedCount: number; editedRouteNames?: string[] };
}

/** Full version incl. the verbatim edit delta (to reopen / export). */
export interface NetworkScenario extends NetworkVersionSummary {
  overlay: NetworkExportOverlayPayload;
}

export interface SaveScenarioInput {
  name: string;
  overlay: NetworkExportOverlayPayload;
  baseVersion?: string;
}

export async function listScenarios(feedId: string, signal?: AbortSignal): Promise<NetworkVersionSummary[]> {
  const res = await atlasBackendApi.get<{ scenarios: NetworkVersionSummary[] }>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/scenarios`,
    { headers: authHeaders(), timeout: TIMEOUT, signal },
  );
  return res.data.scenarios ?? [];
}

export async function getScenario(feedId: string, scenarioId: string, signal?: AbortSignal): Promise<NetworkScenario> {
  const res = await atlasBackendApi.get<NetworkScenario>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/scenarios/${encodeURIComponent(scenarioId)}`,
    { headers: authHeaders(), timeout: TIMEOUT, signal },
  );
  return res.data;
}

export async function saveScenario(feedId: string, input: SaveScenarioInput): Promise<NetworkVersionSummary> {
  const res = await atlasBackendApi.post<NetworkVersionSummary>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/scenarios`,
    { name: input.name, overlay: input.overlay, baseVersion: input.baseVersion },
    { headers: authHeaders(), timeout: TIMEOUT },
  );
  return res.data;
}

export async function deleteScenario(feedId: string, scenarioId: string): Promise<void> {
  await atlasBackendApi.delete(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/scenarios/${encodeURIComponent(scenarioId)}`,
    { headers: authHeaders(), timeout: TIMEOUT },
  );
}

export type Bbox = [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]

export interface NetworkCatalogFeed {
  feedId: string;
  version?: string;
  onestopId?: string | null;
  bbox?: number[];
  counts?: Record<string, number>;
  agencyTimezone?: string | null;
  updatedAt?: number;
}

export interface NetworkRouteOverview {
  routeId: string;
  routeShortName?: string | null;
  routeLongName?: string | null;
  routeColor?: string | null;
  routeType?: number | null;
  polyline: string;
}

export interface NetworkRoutesResponse {
  routes: NetworkRouteOverview[];
  total: number;
  truncated: boolean;
}

export interface NetworkStop {
  stopId: string;
  stopName?: string | null;
  stopLat: number;
  stopLon: number;
  routeCount?: number;
}

export interface RouteVariant {
  directionId: number;
  shapeId?: string | null;
  headsign?: string | null;
  tripCount: number;
}

export interface NetworkRouteDetail {
  routeId: string;
  routeShortName?: string | null;
  routeLongName?: string | null;
  routeType?: number | null;
  routeColor?: string | null;
  directions: Array<{ directionId: number; headsign?: string | null; tripCount: number }>;
  variants: RouteVariant[];
  serviceDays: string[];
}

export interface RouteShape {
  routeId: string;
  directionId: number;
  routeShortName?: string | null;
  routeColor?: string | null;
  routeType?: number | null;
  coordinates: Array<[number, number]>;
  stops: NetworkStop[];
}

/** Lightweight route row for the Fixed Routes drawer's live-network section (PRD-04 FR13). */
export interface NetworkRouteListItem {
  routeId: string;
  routeShortName?: string | null;
  routeLongName?: string | null;
  routeType?: number | null;
  routeColor?: string | null;
}

export interface NetworkRouteListResponse {
  routes: NetworkRouteListItem[];
  total: number;
}

export interface NetworkStopTime {
  stopId: string;
  stopSequence: number;
  arrivalS: number | null; // seconds since midnight (may exceed 86400 for after-midnight)
  departureS: number | null;
}

export interface NetworkTrip {
  tripId: string;
  headsign?: string | null;
  startS: number | null;
  stopTimes: NetworkStopTime[];
}

export interface RouteTrips {
  routeId: string;
  day: string;
  direction: number;
  trips: NetworkTrip[];
  tripCount: number;
  headwayMin: number | null;
}

const TIMEOUT = 20000;

function bboxParam(bbox: Bbox): string {
  return bbox.join(',');
}

export async function getCatalog(signal?: AbortSignal): Promise<NetworkCatalogFeed[]> {
  const res = await atlasBackendApi.get<{ feeds: NetworkCatalogFeed[] }>(`${API_BASE}/catalog`, {
    headers: authHeaders(),
    timeout: TIMEOUT,
    signal,
  });
  return res.data.feeds ?? [];
}

export async function getNetworkRoutes(
  feedId: string,
  bbox: Bbox,
  zoom: number,
  opts: { version?: string; signal?: AbortSignal } = {},
): Promise<NetworkRoutesResponse> {
  const res = await atlasBackendApi.get<NetworkRoutesResponse>(`${API_BASE}/feeds/${encodeURIComponent(feedId)}/network-routes`, {
    headers: authHeaders(),
    params: { bbox: bboxParam(bbox), zoom, version: opts.version },
    timeout: TIMEOUT,
    signal: opts.signal,
  });
  return res.data;
}

export async function getStops(
  feedId: string,
  bbox: Bbox,
  opts: { important?: boolean; limit?: number; version?: string; signal?: AbortSignal } = {},
): Promise<NetworkStop[]> {
  const res = await atlasBackendApi.get<{ stops: NetworkStop[] }>(`${API_BASE}/feeds/${encodeURIComponent(feedId)}/stops`, {
    headers: authHeaders(),
    params: { bbox: bboxParam(bbox), important: opts.important, limit: opts.limit, version: opts.version },
    timeout: TIMEOUT,
    signal: opts.signal,
  });
  return res.data.stops ?? [];
}

/**
 * List ALL routes in a feed (lightweight: id/name/type/color), paginated + optional `q`.
 * Powers the Fixed Routes drawer's live-network section; stops/geometry load lazily per route.
 */
export async function listRoutes(
  feedId: string,
  opts: { q?: string; limit?: number; offset?: number; version?: string; signal?: AbortSignal } = {},
): Promise<NetworkRouteListResponse> {
  const res = await atlasBackendApi.get<NetworkRouteListResponse>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/routes`,
    {
      headers: authHeaders(),
      params: { q: opts.q || undefined, limit: opts.limit, offset: opts.offset, version: opts.version },
      timeout: TIMEOUT,
      signal: opts.signal,
    },
  );
  return res.data;
}

export async function getRouteDetail(
  feedId: string,
  routeId: string,
  opts: { version?: string; signal?: AbortSignal } = {},
): Promise<NetworkRouteDetail> {
  const res = await atlasBackendApi.get<NetworkRouteDetail>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/routes/${encodeURIComponent(routeId)}`,
    { headers: authHeaders(), params: { version: opts.version }, timeout: TIMEOUT, signal: opts.signal },
  );
  return res.data;
}

export async function getRouteShape(
  feedId: string,
  routeId: string,
  opts: { direction?: number; shapeId?: string; routeIds?: string[]; version?: string; signal?: AbortSignal } = {},
): Promise<RouteShape[]> {
  const res = await atlasBackendApi.get<{ routes: RouteShape[] }>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/routes/${encodeURIComponent(routeId)}/shape`,
    {
      headers: authHeaders(),
      params: {
        direction: opts.direction,
        shapeId: opts.shapeId,
        route_ids: opts.routeIds?.join(','),
        version: opts.version,
      },
      timeout: TIMEOUT,
      signal: opts.signal,
    },
  );
  return res.data.routes ?? [];
}

/**
 * Trips for a route on a given day-of-week (or ISO date) + direction, with headway —
 * served by the backend's `trips_by_day_direction` (PRD-05). Times are integer seconds.
 */
export async function getRouteTrips(
  feedId: string,
  routeId: string,
  opts: { day: string; direction?: number; version?: string; signal?: AbortSignal },
): Promise<RouteTrips> {
  const res = await atlasBackendApi.get<RouteTrips>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/routes/${encodeURIComponent(routeId)}/trips`,
    {
      headers: authHeaders(),
      params: { day: opts.day, direction: opts.direction, version: opts.version },
      timeout: TIMEOUT,
      signal: opts.signal,
    },
  );
  return res.data;
}

// --- full edited-network TransXChange export (PRD-05b) ----------------------

export type NetworkExportScope = 'full' | 'changed';

export interface NetworkExportOperator {
  nationalOperatorCode?: string;
  operatorName?: string;
  licenceNumber?: string;
}

/** The merge overlay POSTed at export time (see `networkExportOverlay.buildNetworkExportOverlay`). */
export interface NetworkExportOverlayPayload {
  feed: unknown;
  replacedGtfsRouteIds: string[];
  removedGtfsRouteIds: string[];
  atcoByStopId: Record<string, string>;
}

export interface NetworkExportStatus {
  state: 'exporting' | 'ready' | 'failed';
  step?: string;
  scope?: NetworkExportScope;
  total?: number;
  routesWritten?: number;
  routesSkipped?: number;
  editedCount?: number;
  newCount?: number;
  removedCount?: number;
  counts?: Record<string, number>;
  warnings?: string[];
  sizeBytes?: number;
  filename?: string;
  downloadUrl?: string;
  error?: string;
}

export interface ExportEditedNetworkOptions {
  scope: NetworkExportScope;
  /** Export a SAVED version instead of the POSTed overlay (PRD-05c). */
  scenarioId?: string;
  operator?: NetworkExportOperator;
  feedName?: string;
  /** xsd:dateTime stamp for the TransXChange header (pass in; date APIs vary). */
  creationDateTime?: string;
  /** Fallback service window for routes with none (`YYYY-MM-DD` or `YYYYMMDD`). */
  defaultStartDate?: string;
  defaultEndDate?: string;
  /** Polled status callback (drives the dialog progress). */
  onProgress?: (status: NetworkExportStatus) => void;
  /** Status poll interval (ms). */
  pollInterval?: number;
  signal?: AbortSignal;
}

export interface ExportEditedNetworkResult {
  blob: Blob;
  filename: string;
  status: NetworkExportStatus;
}

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });

/**
 * Export `(hosted feed @ version) ⊕ overlay` to a BODS-shaped TransXChange `.zip`.
 *
 * Kicks the async server job, polls its status (forwarding progress via `onProgress`),
 * then downloads the zip as a Blob the caller saves with `downloadBlob`. Robust for the
 * large full-network case: the work runs server-side and survives ALB idle timeouts.
 */
export async function exportEditedNetwork(
  feedId: string,
  version: string | undefined,
  overlay: NetworkExportOverlayPayload,
  options: ExportEditedNetworkOptions,
): Promise<ExportEditedNetworkResult> {
  const start = await atlasBackendApi.post<{ jobId: string; state: string; scope: NetworkExportScope }>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/export/transxchange`,
    {
      version,
      scope: options.scope,
      overlay,
      scenarioId: options.scenarioId,
      operator: options.operator,
      feedName: options.feedName,
      creationDateTime: options.creationDateTime,
      defaultStartDate: options.defaultStartDate,
      defaultEndDate: options.defaultEndDate,
    },
    { headers: authHeaders(), timeout: TIMEOUT, signal: options.signal },
  );
  const jobId = start.data.jobId;
  const interval = options.pollInterval ?? 1200;

  let status: NetworkExportStatus | null = null;
  let consecutiveErrors = 0;
  // Cap the wait so a wedged job can't poll forever (national export is multi-minute).
  for (let i = 0; i < 5000; i += 1) {
    await delay(interval, options.signal);
    try {
      const res = await atlasBackendApi.get<NetworkExportStatus>(
        `${API_BASE}/feeds/${encodeURIComponent(feedId)}/export-status/${encodeURIComponent(jobId)}`,
        { headers: authHeaders(), timeout: TIMEOUT, signal: options.signal },
      );
      status = res.data;
      consecutiveErrors = 0;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError' || (err as { name?: string }).name === 'CanceledError') {
        throw err;
      }
      // A transient blip (status doc caught mid-write, momentary 404/500) shouldn't kill a
      // multi-minute export — keep polling, but give up after several in a row.
      if (++consecutiveErrors >= 8) throw err;
      continue;
    }
    options.onProgress?.(status);
    if (status.state === 'ready') break;
    if (status.state === 'failed') throw new Error(status.error || 'Export failed');
  }
  if (!status || status.state !== 'ready') {
    throw new Error('Export timed out');
  }

  const dl = await atlasBackendApi.get(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/export-download/${encodeURIComponent(jobId)}`,
    { headers: authHeaders(), responseType: 'blob', timeout: 600000, signal: options.signal },
  );
  return {
    blob: dl.data as Blob,
    filename: status.filename || 'network.transxchange.zip',
    status,
  };
}

export async function searchNetwork(
  feedId: string,
  q: string,
  type: 'all' | 'route' | 'stop' = 'all',
  opts: { limit?: number; signal?: AbortSignal } = {},
): Promise<{ routes: Array<Record<string, unknown>>; stops: Array<Record<string, unknown>> }> {
  const res = await atlasBackendApi.get<{ routes: Array<Record<string, unknown>>; stops: Array<Record<string, unknown>> }>(
    `${API_BASE}/feeds/${encodeURIComponent(feedId)}/search`,
    { headers: authHeaders(), params: { q, type, limit: opts.limit }, timeout: TIMEOUT, signal: opts.signal },
  );
  return res.data;
}
